import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import React from 'react';
import { useConnect } from 'redux-bundler-hook';
import {
  COMPLIANCE,
  GENERAL,
  HATCH_APP_ROUTE,
  isLocalServer,
  TEAM,
  WORKSPACE,
} from 'root/stores/routes/APP_URLS';
import isCorrectHatchIdFormat from 'utils/isCorrectHatchIdFormat';
import urlFormatter from 'utils/urlFormatter';

import DefaultTemplateLeftColumnButton from './components/DefaultTemplateLeftColumnButton';
import DefaultTemplateLeftColumnUserDetails from './components/DefaultTemplateLeftColumnUserDetails';
import {
  StyledBoardIcon,
  StyledCustomLogo,
  StyledDefaultTemplateLeftColumn,
  StyledHome,
  StyledLogoFull,
  StyledLogout,
  StyledScrollableColumn,
  StyledShare,
  StyledShield,
  StyledTeam,
} from './styles';

export const NAVIGATION_ROUTES = [
  {
    icon: StyledShare,
    isBackToHatch: true,
    title: 'Back to Hatch',
    url: HATCH_APP_ROUTE,
  },
  {
    icon: StyledHome,
    title: 'General',
    url: GENERAL,
  },
  {
    icon: StyledBoardIcon,
    title: 'Workspaces',
    url: WORKSPACE,
  },
  {
    icon: StyledTeam,
    title: 'Team',
    url: TEAM,
  },
  {
    featureFlag: '10LdcCompliance',
    icon: StyledShield,
    title: 'Compliance',
    url: COMPLIANCE,
  },
  {
    icon: StyledLogout,
    isLogoutButton: true,
    title: 'Logout',
    url: '',
  },
];

const DefaultTemplateLeftColumn = ({
  doToggleMobileSidebarExpanded,
  mobileSidebarExpanded,
}) => {
  const {
    doDeleteSession,
    doUpdateUrl,
    routeInfo: {
      pattern,
      params: { organizationId },
    },
    defaultOrganizationId,
    currentOrganization: { logo },
  } = useConnect(
    'doDeleteSession',
    'doUpdateUrl',
    'selectRouteInfo',
    'selectDefaultOrganizationId',
    'selectCurrentOrganization',
  );
  const flags = useFlags();
  const menuItems = NAVIGATION_ROUTES.filter(
    (item) =>
      isLocalServer ||
      !item.featureFlag ||
      (item.featureFlag && flags[item.featureFlag]),
  );

  return (
    <StyledDefaultTemplateLeftColumn>
      {logo ? (
        <StyledCustomLogo src="https://s3.amazonaws.com/assets.enjin.com/wall_embed_images/1509132281_C1k9PtEXEAQHJuK.jpg" />
      ) : (
        <StyledLogoFull />
      )}
      <StyledScrollableColumn>
        {organizationId
          ? menuItems.map(
              ({ icon, isLogoutButton, title, url, isBackToHatch }) => {
                const urlWithOrganizationId = urlFormatter({
                  organizationId: isCorrectHatchIdFormat(organizationId)
                    ? organizationId
                    : defaultOrganizationId,
                  url,
                });

                const handleClick = () => {
                  // if logout button, delete session and redirect to V2
                  if (isLogoutButton) {
                    return doDeleteSession();
                  }

                  // if back to hatch button, reidrect to V2
                  if (isBackToHatch) {
                    return window.location.replace(HATCH_APP_ROUTE);
                  }

                  // otherwise update URL inside app
                  doUpdateUrl(urlWithOrganizationId);
                  if (mobileSidebarExpanded) {
                    window.setTimeout(
                      () => doToggleMobileSidebarExpanded({ expanded: false }),
                      100,
                    );
                  }

                  return null;
                };

                return (
                  <DefaultTemplateLeftColumnButton
                    active={url === pattern}
                    data-test-id="cb585aca-6c53-4fd4-89e6-0bfd3d30c80c"
                    icon={icon}
                    isLogoutButton={isLogoutButton}
                    key={title}
                    onClick={handleClick}
                    title={title}
                    url={urlWithOrganizationId}
                  />
                );
              },
            )
          : null}
      </StyledScrollableColumn>
      <DefaultTemplateLeftColumnUserDetails />
    </StyledDefaultTemplateLeftColumn>
  );
};

DefaultTemplateLeftColumn.propTypes = {
  doToggleMobileSidebarExpanded: PropTypes.func.isRequired,
  mobileSidebarExpanded: PropTypes.bool,
};

DefaultTemplateLeftColumn.defaultProps = {
  mobileSidebarExpanded: false,
};

export default DefaultTemplateLeftColumn;
